import React from 'react';
import { Clock, Trash } from '@saleshandy/icons';
import { useHistory } from 'react-router-dom';
import toaster from '../../../../shared/toaster';
import {
  capitalizeFirstLetter,
  getTimeAgoString,
  mapObjectToString,
  resolveRecentSearchDisplayPayload,
} from '../../helpers/helper';
import { LEAD_RECENT_SEARCH_MESSAGES } from '../../helpers/leads-message';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { StorageUtil } from '../../../../shared/utils/storage';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import {
  getCompanyIdNameMap,
  pruneCompanyMap,
  extractCompanyIdsFromPayloads,
  getAllRecentSearchPayloads,
} from '../../utils/company-id-name-map';
import AiSearchBar from '../ai-search-bar';

export default function RecentSearch({
  storedUrls,
  setStoredUrls,
  loginUserEmail,
  searchLeads,
  searchCompanies,
  onRestoreCompanyFilterUI,
  onRestorePeopleFilterUI,
  onFetchPeopleCounts,
  storageKey,
  companyListsData,
  peopleListsData,
}) {
  const history = useHistory();
  const companyIdNameMap = getCompanyIdNameMap(loginUserEmail || '');

  const deleteUrlByUrl = (urlToDelete) => {
    const updatedUrls = storedUrls.filter((url) => url.url !== urlToDelete);

    setStoredUrls(updatedUrls);

    const allStoredUrls = JSON.parse(StorageUtil.getItem(storageKey) || '{}');
    allStoredUrls[loginUserEmail] = updatedUrls;
    StorageUtil.setItem(storageKey, JSON.stringify(allStoredUrls));

    // Prune company ID->name map: keep only IDs still in recent searches
    if (loginUserEmail) {
      const allPayloads = getAllRecentSearchPayloads(loginUserEmail);
      const allCompanyIds = extractCompanyIdsFromPayloads(allPayloads);
      pruneCompanyMap(loginUserEmail, allCompanyIds);
    }

    return toaster.success(
      LEAD_RECENT_SEARCH_MESSAGES.recent_search_delete_msg,
    );
  };

  const getFilterString = (urlEntry) =>
    mapObjectToString(
      resolveRecentSearchDisplayPayload(urlEntry),
      companyListsData,
      peopleListsData,
      companyIdNameMap,
    );

  const getFirstValue = (urlEntry) => {
    const filterString = getFilterString(urlEntry);
    const firstValue = filterString.split(',')[0];
    if (firstValue.length > 100) {
      return `${firstValue.substring(0, 100)}...`;
    }
    return capitalizeFirstLetter(firstValue);
  };

  const getRemainingValues = (urlEntry) => {
    const filterString = getFilterString(urlEntry);
    if (filterString.split(',').length > 4) {
      return `${filterString.split(',').slice(1, 4).join(',')}...`;
    }
    const remainingValues = filterString.split(',').slice(1, 4).join(',');
    return remainingValues;
  };

  return (
    <>
      <div className="recent-search">
        <div className="d-flex flex-column align-items-center h-100">
          <div className="recent-search-container">
            <div>
              <AiSearchBar />
            </div>
            <div>
              <p className="service-activity">Recent Searches</p>
              <p className="latest-activity">
                {storedUrls?.length > 0
                  ? 'Your latest prospecting activity'
                  : 'No recent search found!'}
              </p>
            </div>
            <div
              className={`${
                storedUrls?.length === 0 ? 'table-nodata' : 'table-border'
              }`}
            >
              {storedUrls &&
                storedUrls?.map((url) => (
                  <React.Fragment key={url.timestamp || url.url}>
                    <div
                      className="table-data"
                      {...accessibleOnClick(() => {
                        history.push(url?.url);
                        const isCompanyUrl = url.url.includes(
                          '/v2/leads/company/',
                        );
                        if (isCompanyUrl) {
                          if (url?.pendingFiltersStr) {
                            onRestoreCompanyFilterUI?.(
                              new URLSearchParams(url.pendingFiltersStr),
                            );
                            // Bulk entries have no URL params, so the location effect
                            // won't auto-fire — call explicitly here
                            searchCompanies?.(url?.payload);
                          }
                          // Normal URL searches: useCompanyFinder location.search effect handles it
                        } else if (url?.pendingFiltersStr) {
                          onRestorePeopleFilterUI?.(
                            new URLSearchParams(url.pendingFiltersStr),
                          );
                          // Bulk entries have no URL params, so the location effect
                          // won't auto-fire counts — call it explicitly here
                          onFetchPeopleCounts?.(url?.payload);
                          searchLeads?.(url?.payload);
                          // Normal URL searches: useLeadFinder location.search effect handles it
                        }
                      })}
                    >
                      <div className="d-flex align-items-center">
                        <div>
                          <div className="clock-bg">
                            <Clock className="clock-icon" />
                          </div>
                        </div>
                        <div className="prospect-details">
                          <div className="d-flex align-items-center">
                            <OverlayTooltip
                              text={getFilterString(url)}
                              placement={Placement.Top}
                            >
                              <p className="bold-text w-100 mb-0">
                                {getFirstValue(url)}
                                {getFilterString(url).split(',')?.length > 1 &&
                                  ','}{' '}
                                <span className="normal-text mb-0">
                                  {getRemainingValues(url)}
                                </span>
                              </p>
                            </OverlayTooltip>
                          </div>
                          <p className="mb-0 days">
                            {getTimeAgoString(url?.timestamp)}
                          </p>
                        </div>
                      </div>
                      <div>
                        <Trash
                          className="Trash-icon ml-2 cursor-pointer"
                          onClick={(e) => {
                            e?.stopPropagation();
                            deleteUrlByUrl(url?.url);
                          }}
                        />
                      </div>
                    </div>
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
